// Patient messages — multi-conversation inbox. Two panes: a care-team conversation
// sidebar + the active thread. "New message" opens a start-chat popout (pick an assigned doctor →
// first message). Data comes from the conversation endpoints via a thin local fetch client (NOT the
// published SDK). Bodies are Class-B PHI: rendered here only, never logged. v1 has no unread badges.
import { useEffect, useRef, useState } from "react";
import { Modal, Avatar } from "@championcare/ui";
import { DoctorCardModal } from "../components/DoctorCard";
import { useAsync } from "../lib/useAsync";
import { formatDateTime } from "../lib/format";
import { Loading, ErrorState } from "../components/ui";
import {
  useConversationsApi,
  ConvApiError,
  type ConversationsApi,
  type ConversationSummary,
} from "../lib/conversationsApi";

const SendIcon = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="22" y1="2" x2="11" y2="13" /><polygon points="22 2 15 22 11 13 2 9 22 2" />
  </svg>
);
const ComposeIcon = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
  </svg>
);
const BackIcon = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="15 18 9 12 15 6" />
  </svg>
);
const ClockIcon = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="12" cy="12" r="9" />
    <polyline points="12 7 12 12 15.5 14" />
  </svg>
);

const initials = (first: string, last: string) =>
  `${first.charAt(0)}${last.charAt(0)}`.toUpperCase();
const docName = (c: ConversationSummary) =>
  c.doctor ? `Dr. ${c.doctor.first_name} ${c.doctor.last_name}` : "Care team";
const docInitials = (c: ConversationSummary) =>
  c.doctor ? initials(c.doctor.first_name, c.doctor.last_name) : "CC";

// Compact timestamp for the sidebar: time if today, else short date.
function shortWhen(iso: string): string {
  const d = new Date(iso);
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  return sameDay
    ? d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })
    : d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

export function Messages() {
  const api = useConversationsApi();
  const { data, loading, error, reload } = useAsync(() => api.conversations(), [], { pollMs: 5000 });
  const [active, setActive] = useState<string | null>(null);
  const [showNew, setShowNew] = useState(false);

  const conversations = data ?? [];
  // Desktop shows the first thread when none is tapped; mobile stays on the list until tapped
  // (CSS hides the pane unless `.m-chat`, which is only set when `active` is non-null).
  const currentId = active ?? conversations[0]?.thread_id ?? null;
  const current = conversations.find((c) => c.thread_id === currentId) ?? null;

  if (loading) return <Loading />;
  if (error) return <ErrorState message={error} />;

  return (
    <>
      <div className="app-page-head">
        <h1>Your <em>messages.</em></h1>
        <p>
          Direct, secure messaging with your care team.
          <br />
          <br />
          <strong>Please Note:</strong> This service is not intended for emergency medical care. If you
          are experiencing a medical emergency, please call <a href="tel:911">911</a> or go to the
          nearest emergency department right away.
        </p>
      </div>

      <div className={`pt-inbox ${active ? "m-chat" : ""}`}>
        {/* ===== Conversation sidebar ===== */}
        <aside className="pt-inbox__list">
          <div className="pt-inbox__top">
            <h3 className="pt-inbox__title">Inbox</h3>
            <button
              className="pt-compose-btn"
              onClick={() => setShowNew(true)}
              aria-label="Start a new message"
              title="New message"
            >
              {ComposeIcon}
            </button>
          </div>
          {conversations.length ? (
            <div className="pt-threads">
              {conversations.map((c) => (
                <button
                  key={c.thread_id}
                  className={`pt-thread ${currentId === c.thread_id ? "is-active" : ""}`}
                  onClick={() => setActive(c.thread_id)}
                >
                  <Avatar className="pt-thread__avatar" src={c.doctor?.avatar_url} initials={docInitials(c)} />
                  <span className={`pt-thread__main ${c.unread > 0 ? "has-unread" : ""}`}>
                    <span className="pt-thread__row">
                      <span className="pt-thread__name">{docName(c)}</span>
                      {c.last_message && (
                        <span className="pt-thread__time">{shortWhen(c.last_message.created_at)}</span>
                      )}
                    </span>
                    <span className="pt-thread__preview">
                      {c.last_message?.preview ?? (c.subject ? c.subject : "No messages yet")}
                    </span>
                  </span>
                  {c.unread > 0 && (
                    <span className="pt-thread__unread">{c.unread > 99 ? "99+" : c.unread}</span>
                  )}
                </button>
              ))}
            </div>
          ) : (
            <div className="pt-threads pt-threads--empty">
              No conversations yet. Start one with your care team.
            </div>
          )}
        </aside>

        {/* ===== Active thread ===== */}
        <section className="pt-inbox__pane">
          {current ? (
            <Thread
              key={current.thread_id}
              api={api}
              summary={current}
              onBack={() => setActive(null)}
            />
          ) : (
            <div className="pt-chat__empty">
              {conversations.length
                ? "Select a conversation."
                : "Start a conversation to begin messaging your care team."}
            </div>
          )}
        </section>
      </div>

      {showNew && (
        <NewChatModal
          api={api}
          onClose={() => setShowNew(false)}
          onCreated={(threadId) => {
            setShowNew(false);
            setActive(threadId);
            reload();
          }}
        />
      )}
    </>
  );
}

function Thread({
  api,
  summary,
  onBack,
}: {
  api: ConversationsApi;
  summary: ConversationSummary;
  onBack: () => void;
}) {
  const threadId = summary.thread_id;
  const { data, loading, error, reload } = useAsync(() => api.messages(threadId), [threadId], {
    pollMs: 5000,
  });
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const [cardOpen, setCardOpen] = useState(false);
  const listRef = useRef<HTMLDivElement>(null);
  const messages = data?.messages ?? [];
  // Show the "responds within 24h" notice only while the patient is awaiting a reply — i.e. the
  // last message in the thread is theirs. Hidden once the care team responds, and on an empty thread.
  const last = messages[messages.length - 1];
  const awaitingReply = !!last && (last.sender_role === "patient" || last.sender_role === "me");

  useEffect(() => {
    const el = listRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages.length]);

  async function send() {
    if (!draft.trim() || busy) return;
    setBusy(true);
    try {
      await api.sendMessage(threadId, draft.trim());
      setDraft("");
      reload();
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <div className="pt-chat__head">
        <button className="pt-chat__back" onClick={onBack} aria-label="Back to conversations">
          {BackIcon}
        </button>
        {summary.doctor ? (
          <button
            className="pt-chat__whobtn"
            onClick={() => setCardOpen(true)}
            aria-label={`View ${docName(summary)}'s profile`}
          >
            <Avatar className="pt-chat__avatar" src={summary.doctor.avatar_url} initials={docInitials(summary)} />
            <span className="pt-chat__whoinfo">
              <span className="pt-chat__who">{docName(summary)}</span>
              <span className="pt-chat__status">
                {summary.subject ? summary.subject : "Secure & encrypted"}
              </span>
            </span>
          </button>
        ) : (
          <>
            <Avatar className="pt-chat__avatar" src={null} initials={docInitials(summary)} />
            <div>
              <div className="pt-chat__who">{docName(summary)}</div>
              <div className="pt-chat__status">
                {summary.subject ? summary.subject : "Secure & encrypted"}
              </div>
            </div>
          </>
        )}
      </div>
      {cardOpen && summary.doctor && (
        <DoctorCardModal doctorId={summary.doctor.id} onClose={() => setCardOpen(false)} />
      )}

      {loading && !data ? (
        <div className="pt-chat__empty"><Loading /></div>
      ) : error ? (
        <div className="pt-chat__empty"><ErrorState message={error} /></div>
      ) : messages.length ? (
        <div className="pt-chat__list" ref={listRef}>
          {messages.map((m) => (
            <div key={m.id} className={`pt-msg pt-msg--${m.sender_role}`}>
              <div className="pt-msg__bubble">{m.body}</div>
              <div className="pt-msg__time">{formatDateTime(m.created_at)}</div>
            </div>
          ))}
          {/* Patient is waiting on a reply → set the 24h expectation, centered under their last
              message. Disappears once the care team responds (THU-310 demo placeholder). No PHI. */}
          {awaitingReply && (
            <div className="pt-chat__await" role="note">
              {ClockIcon}
              <span>Your care team typically responds within 24 hours.</span>
            </div>
          )}
        </div>
      ) : (
        <div className="pt-chat__empty">No messages yet. Say hello to {docName(summary)}.</div>
      )}

      <div className="pt-chat__compose">
        <input
          className="pt-chat__input"
          // land the patient ready to type when a thread opens
          autoFocus
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && send()}
          placeholder="Write a message…"
          aria-label="Message"
        />
        <button className="pt-chat__send" disabled={!draft.trim() || busy} onClick={send} aria-label="Send">
          {SendIcon}
        </button>
      </div>
    </>
  );
}

function NewChatModal({
  api,
  onClose,
  onCreated,
}: {
  api: ConversationsApi;
  onClose: () => void;
  onCreated: (threadId: string) => void;
}) {
  const careTeam = useAsync(() => api.careTeam(), []);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const team = careTeam.data ?? [];

  // Click a doctor → open (or get-or-create) the thread and drop straight into the chat.
  async function start(doctorId: string) {
    if (busyId) return;
    setBusyId(doctorId);
    setErr(null);
    try {
      const { thread_id } = await api.createConversation({ doctor_id: doctorId });
      onCreated(thread_id);
    } catch (e) {
      setErr(e instanceof ConvApiError ? e.message : "Could not start the conversation. Please try again.");
      setBusyId(null);
    }
  }

  return (
    <Modal open onClose={onClose} labelledBy="newChatTitle">
      <div className="pt-newchat-title" id="newChatTitle">
        New <em>message</em>
      </div>
      <p className="pt-newchat-sub">Choose a doctor on your care team to start a conversation.</p>

      {err && <div className="pt-newchat-error" role="alert">{err}</div>}

      {careTeam.loading ? (
        <Loading />
      ) : careTeam.error ? (
        <ErrorState message={careTeam.error} />
      ) : team.length ? (
        <div className="pt-doc-picker">
          {team.map((d) => (
            <button
              key={d.id}
              type="button"
              className="pt-doc-card"
              disabled={Boolean(busyId)}
              onClick={() => start(d.id)}
            >
              <Avatar className="pt-doc-card__avatar" src={d.avatar_url} initials={initials(d.first_name, d.last_name)} />
              <span className="pt-doc-card__main">
                <span className="pt-doc-card__name">Dr. {d.first_name} {d.last_name}</span>
                {d.specialty && <span className="pt-doc-card__spec">{d.specialty}</span>}
              </span>
              <span className="pt-doc-card__go">{busyId === d.id ? "…" : "→"}</span>
            </button>
          ))}
        </div>
      ) : (
        <div className="pt-newchat-empty">No care-team doctors are assigned to you yet.</div>
      )}
    </Modal>
  );
}
